'use client';
import { useEffect, useRef } from 'react';
import gsap from 'gsap';

const useAnimatedText = (deps = []) => {
	const hasInitialized = useRef(false);

	useEffect(() => {
		// Prevent multiple initializations
		if (hasInitialized.current) return;

		// Only run animation on screens wider than 767px (desktop)
		if (window.innerWidth <= 767) {
			// On mobile, ensure elements are visible
			const allElements = [
				...gsap.utils.toArray('.anim-text-title'),
				...gsap.utils.toArray('.fade-up')
			];
			allElements.forEach(el => {
				gsap.set(el, { opacity: 1, y: 0 });
			});
			return;
		}

		// Use requestAnimationFrame to defer animation setup after initial paint
		const setupAnimations = () => {
			const elements = gsap.utils.toArray('.anim-text-title');
			const elementsFadeup = gsap.utils.toArray('.fade-up');

			// Check if elements are above the fold (likely LCP candidates)
			const isAboveFold = (el) => {
				const rect = el.getBoundingClientRect();
				return rect.top < window.innerHeight * 0.5; // Top 50% of viewport
			};

			// Set initial states more carefully
			const setupElement = (el, isTitle = false) => {
				if (isAboveFold(el)) {
					// For above-fold elements, start with minimal opacity reduction
					gsap.set(el, { opacity: 0.1, y: 20 });
					// Quick fade-in for LCP elements
					gsap.to(el, {
						opacity: 1,
						y: 0,
						duration: 0.3,
						ease: 'power2.out'
					});
				} else {
					// Below fold elements can use the full animation
					gsap.set(el, { opacity: 0, y: 50 });
				}
			};

			// Setup initial states
			elements.forEach(el => setupElement(el, true));
			elementsFadeup.forEach(el => setupElement(el, false));

			// Create scroll-triggered animations only for below-fold elements
			const triggers = elements
				.filter(el => !isAboveFold(el))
				.map(el =>
					gsap.fromTo(
						el,
						{ y: 50, opacity: 0 },
						{
							y: 0,
							opacity: 1,
							duration: 1.5, // Slightly faster
							ease: 'power3.out', // Less aggressive easing
							scrollTrigger: {
								trigger: el,
								start: 'top 85%', // Trigger slightly earlier
								end: 'bottom top',
								toggleActions: 'play none none reverse',
							},
						}
					)
				);

			const triggersElements = elementsFadeup
				.filter(el => !isAboveFold(el))
				.map(el =>
					gsap.fromTo(
						el,
						{ y: 50, opacity: 0 },
						{
							y: 0,
							opacity: 1,
							duration: 1.5,
							ease: 'power3.out',
							scrollTrigger: {
								trigger: el,
								start: 'top 90%',
								end: 'bottom top',
								toggleActions: 'play none none reverse',
							},
						}
					)
				);

			// Store cleanup function
			return () => {
				triggers.forEach(trigger => {
					if (trigger.scrollTrigger) {
						trigger.scrollTrigger.kill();
					}
				});
				triggersElements.forEach(trigger => {
					if (trigger.scrollTrigger) {
						trigger.scrollTrigger.kill();
					}
				});
			};
		};

		// Defer setup to avoid blocking initial paint
		const timeoutId = setTimeout(setupAnimations, 0);
		hasInitialized.current = true;

		return () => {
			clearTimeout(timeoutId);
			hasInitialized.current = false;
		};
	}, deps);
};

export default useAnimatedText;